/**
 * Ephemeral runtime flag: is an onboarding tour step on screen *right now*?
 *
 * Distinct from `onboardingTourState`, which persists per-tour progress in
 * localStorage. This is in-memory only and answers a different question — "does
 * the tour currently own the screen" — so lower-priority app-level prompts can
 * step aside while it does. The Intercom consent banner reads it: the tour dims
 * the page and anchors a card, and a second card underneath that backdrop reads
 * as a collision (two overlays at once). Gating on *active* rather than on
 * *onboarding done* matters — a user who never gets a tour must still be asked
 * for consent, and for them this flag simply never flips.
 *
 * A tiny listener set rather than a window event: both producer (the tour hook)
 * and consumer (the banner hook) live in the same document, so there is no
 * cross-tab dimension to mirror, and no storage to fall back on.
 */

let active = false;
const listeners = new Set<() => void>();

/** Set by the tour hook: `true` while a step is anchored, `false` otherwise. */
export function setOnboardingTourActive(next: boolean): void {
  if (active === next) return;
  active = next;
  for (const notify of listeners) notify();
}

export function isOnboardingTourActive(): boolean {
  return active;
}

export function subscribeOnboardingTourActive(
  onChange: () => void,
): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}
